<script lang="ts">
	import Button from "../Button.svelte";
	import LabelledDate from "../Form/LabelledDate.svelte";
	import cake from "../../../../core/assets/icons/cake.svg?raw";
	import LabelledInput from "../Form/LabelledInput.svelte";
	import Checkbox from "../Form/Checkbox.svelte";
	import Form from "../Form/Form.svelte";
	import { getGlobalSettings } from "../../getGlobalSettings.js";
	import { createPromiseStore } from "../../../../core/utils/createPromiseStore.js";
	import { getClients } from "../../appsync/getClients.js";
	import sendInquiry from "../../appsync/mutations/sendInquiry.graphql";
	import Modal from "../Modal.svelte";
	import Smartform from "../Smartform.svelte";
	import GdprCheckbox from "../GdprCheckbox.svelte";
	import { InquiryType } from "../../../../core/schema/InquiryType.js";
	import { initSentry } from "../../utils/initSentry.js";
	import * as Sentry from "@sentry/browser";
	import { expectErrorMessage } from "../Form/expectErrorMessage.js";
	import { nbspify } from "../../../../core/utils/nbspify.js";
	import Message from "../Message.svelte";
	import FileUploader from "../Form/FileUploader.svelte";
	import { acceptedStaticImages } from "../../../../core/schema/acceptedStaticImages";
	import type { UploadedFile } from "../../../../core/schema/UploadedFile";
	import { WeddingCakeDecorationStyle } from "../../../../core/schema/WeddingCakeDecorationStyle";
	import { weddingCakeDecorationStyleNameMap } from "../../../../core/schema/names/weddingCakeDecorationStyleNameMap";
	import { weddingCakeLayersNameMap } from "../../../../core/schema/names/weddingCakeLayersNameMap";
	import { weddingCakeSizeShortNameMap } from "../../../../core/schema/names/weddingCakeSizeNameMap";
	import type { SmartFormAddress } from "../../../../core/schema/SmartFormAddress";
	import { SmartFormAddressValidationResultType, type SmartFormAddressResult } from "../../../../core/schema/Smartform";
	import LabelledRadioInput from "../Form/LabelledRadioInput.svelte";
	import { WeddingCakeLayers } from "../../../../core/schema/WeddingCakeLayers";
	import { WeddingCakeSize } from "../../../../core/schema/WeddingCakeSize";
	import type { InquiryCreateInputOfType } from "../../../../core/schema/public/InquiryCreateInput.js";
	import { weddingCakeLayersToSizesMap } from "../../../../core/schema/weddingCakeLayersToSizesMap.js";
	import { InquiryWeddingDeliveryType } from "../../../../core/schema/InquiryWeddingDeliveryType";

	let asCompany = false;
	let selectedFiles: UploadedFile[] | undefined;
	let smartFormWeddingAddress: SmartFormAddress | undefined;
	let numberOfLayers = WeddingCakeLayers.ThreeLayers;
	let weddingCakeSize: WeddingCakeSize | undefined = undefined;
	let weddingDecorationStyle: WeddingCakeDecorationStyle | undefined = undefined;
	let fileUploaderKey = crypto.randomUUID();

	function onAddressValidation({ detail }: CustomEvent<SmartFormAddressResult>): void {
		if (detail.instanceId !== "smartform-instance-place") {
			return;
		}
		const [address] = detail.result.addressArray;
		if (detail.result.type !== SmartFormAddressValidationResultType.HIT || !address) {
			smartFormWeddingAddress = undefined;
			return;
		}

		smartFormWeddingAddress = address;
	}

	const globalSettings = getGlobalSettings();
	const { TENANT_ID, TENANT_LOCALE } = globalSettings;

	const send = createPromiseStore(async (input: InquiryCreateInputOfType<InquiryType.Wedding>) => {
		const { graphQL } = await getClients(globalSettings);
		await graphQL.mutate<{ input: InquiryCreateInputOfType<InquiryType.Wedding> }>({
			mutation: sendInquiry,
			variables: { input },
		});
	});

	$: sendErrorMessage =
		$send.status === "error" &&
		expectErrorMessage(
			$send.error,
			TENANT_LOCALE,
			{},
			"Chyba při odesílání poptávky. Zkuste to prosím znovu nebo později.",
		);

	let showSuccessModal = false;
	async function submit(event: SubmitEvent): Promise<void> {
		const form = event.target as HTMLFormElement;
		const formData = new FormData(form);
		const weddingAddress = {
			street: String(formData.get("weddingAddressStreet")),
			streetNumber: String(formData.get("weddingAddressStreetNumber")),
			city: String(formData.get("weddingAddressCity")),
			zip: String(formData.get("weddingAddressZip")),
			smartFormAddress: smartFormWeddingAddress,
		};
		const adressEntered = Object.values(weddingAddress).some(Boolean);

		const input: InquiryCreateInputOfType<InquiryType.Wedding> = {
			fullName: String(formData.get("fullName")),
			email: String(formData.get("email")),
			phoneNumber: String(formData.get("phoneNumber")),
			type: InquiryType.Wedding,
			date: String(formData.get("date")),
			weddingDetail: {
				cuttingTime: String(formData.get("cuttingTime")) || undefined,
				decorationStyle: formData.get("decorationStyle") as WeddingCakeDecorationStyle,
				deliveryType:
					formData.get("cakeDeliveredByMyself") === null
						? InquiryWeddingDeliveryType.DeliveryToAddress
						: InquiryWeddingDeliveryType.PickUpUndecided,
				estimatedGuests: Number(formData.get("estimatedGuests")),
				fillingTasteTest: Boolean(formData.get("fillingTasteTest")),
				images: selectedFiles ?? [],
				size: formData.get("size") as WeddingCakeSize,
				weddingLocation: String(formData.get("weddingLocation")) || undefined,
				...(adressEntered && { weddingAddress }),
			},
			billingAddress: {
				street: String(formData.get("street")),
				streetNumber: String(formData.get("streetNumber")),
				city: String(formData.get("city")),
				zip: String(formData.get("zip")),
			},
			...(asCompany && {
				company: {
					name: String(formData.get("cmpName")),
					cin: String(formData.get("cmpCin")),
					vat: String(formData.get("cmpVat")),
				},
			}),
			note: String(formData.get("note")) || undefined,
			tenantId: TENANT_ID,
			gdprApprovedAt: String(formData.get("gdprApprovedAt")),
		};

		if (!smartFormWeddingAddress && adressEntered) {
			sendErrorMessage = "Vyplňte validní adresu.";
			return;
		}

		try {
			await send.call(input);
			numberOfLayers = WeddingCakeLayers.ThreeLayers;
			weddingCakeSize = undefined;
			weddingDecorationStyle = undefined;
			form.reset();
			fileUploaderKey = crypto.randomUUID();
			showSuccessModal = true;
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error(error);
			await initSentry(globalSettings);
			Sentry.captureException(error);
		}
	}

	function setPhotoFile({ detail: { files } }: CustomEvent<{ files: UploadedFile[] | undefined }>): void {
		selectedFiles = files;
	}

	const cakeOptions = {
		[WeddingCakeLayers.OneLayer]: [
			WeddingCakeSize.Portions12,
			WeddingCakeSize.Portions18,
			WeddingCakeSize.Portions25,
			WeddingCakeSize.Portions30,
			WeddingCakeSize.Portions50Double,
		],
		[WeddingCakeLayers.TwoLayers]: [
			WeddingCakeSize.Portions25,
			WeddingCakeSize.Portions30,
			WeddingCakeSize.Portions40,
			WeddingCakeSize.Portions50Double,
			WeddingCakeSize.Portions60,
		],
		[WeddingCakeLayers.ThreeLayers]: [
			WeddingCakeSize.Portions50Triple,
			WeddingCakeSize.Portions60,
			WeddingCakeSize.Portions70,
			WeddingCakeSize.Portions80,
			WeddingCakeSize.Portions90,
		],
		[WeddingCakeLayers.More]: [
			WeddingCakeSize.Portions60,
			WeddingCakeSize.Portions70,
			WeddingCakeSize.Portions80,
			WeddingCakeSize.Portions90,
			WeddingCakeSize.Portions90Plus,
		],
	} satisfies Record<WeddingCakeLayers, WeddingCakeSize[]>;

	/* eslint-disable @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access */
</script>

<Modal class="!max-w-[36rem]" show={showSuccessModal}>
	<p class="h2" slot="title">Děkujeme za poptávku</p>
	<div class="flex flex-col items-center gap-2">
		<p>Vaše poptávka byla úspěšně odeslána. Brzy se vám ozveme.</p>

		<Button
			on:click={() => {
				showSuccessModal = false;
			}}
		>
			Rozumím
		</Button>
	</div>
</Modal>

<Smartform on:addressValidation={onAddressValidation}>
	<Form onsubmit={submit} class="flex flex-col items-center gap-8" enableRecaptcha>
		<div class="w-full">
			<div class="grid grid-cols-1 gap-x-6 gap-y-10 lg:mx-auto lg:max-w-4xl">
				<div class="flex flex-col gap-x-6 gap-y-3">
					<p class="mb-1 font-semibold">Informace o svatbě</p>
					<div class="grid gap-3 sm:grid-cols-2 lg:grid-cols-[55%_auto] xl:grid-cols-2">
						<LabelledDate class="[&>input]:font-semibold" id="date" required min={new Date()} withoutDayOfTheWeek>
							Datum svatby
						</LabelledDate>
						<LabelledInput id="cuttingTime" type="time">Přibližný čas krájení</LabelledInput>
					</div>
					<div class="grid gap-3 sm:grid-cols-2 lg:row-span-2 lg:grid-cols-1 xl:row-span-1 xl:grid-cols-2">
						<LabelledInput id="estimatedGuests" type="number" required inputProps={{ min: 1 }}>
							Přibližný počet hostů
						</LabelledInput>
						<Checkbox
							id="cakeDeliveredByMyself"
							class="relative !mt-0 h-[3.5rem] items-center rounded-[.25rem] border-[0.0625rem] bg-white px-3 text-base text-black lg:px-6"
							labelProps={{
								class:
									"absolute inset-0 w-full h-full cursor-pointer flex items-center pl-10 lg:pl-14 peer-checked:font-semibold",
							}}
						>
							<span class="!mt-[.15rem]">Dort si odvezu sám</span>
						</Checkbox>
					</div>
				</div>

				<div class="flex flex-col gap-x-6 gap-y-3">
					<p class="mb-1 font-semibold">Místo konání svatby</p>
					<LabelledInput id="weddingLocation">Název restaurace / Popis místa</LabelledInput>
					<div class="grid gap-3 sm:grid-cols-[1fr,7.5rem]">
						<LabelledInput
							id="weddingAddressStreet"
							inputProps={{ class: "smartform-address-street smartform-instance-place" }}
						>
							Ulice
						</LabelledInput>
						<LabelledInput
							id="weddingAddressStreetNumber"
							inputProps={{ class: "smartform-address-number smartform-instance-place" }}
						>
							čp.
						</LabelledInput>
					</div>
					<div class="grid gap-3 sm:grid-cols-[1fr,7.5rem]">
						<LabelledInput
							id="weddingAddressCity"
							inputProps={{ class: "smartform-address-city smartform-instance-place" }}
						>
							Město
						</LabelledInput>
						<LabelledInput
							id="weddingAddressZip"
							minlength={3}
							inputProps={{ class: "smartform-address-zip smartform-instance-place" }}
						>
							PSČ
						</LabelledInput>
					</div>
				</div>
				<div class="flex flex-col gap-x-6 gap-y-6">
					<div class="grid grid-cols-2"></div>
					<div>
						<p class="mb-1 font-semibold">Kolik má mít dort pater?</p>
						<div class="mt-4 grid grid-cols-1 gap-x-3 gap-y-3 sm:grid-cols-2 md:grid-cols-4">
							{#each Object.values(WeddingCakeLayers) as layers}
								<LabelledRadioInput
									id="layers-{layers}"
									name="layers"
									value={layers}
									bind:group={numberOfLayers}
									required
									on:change={() => {
										if (numberOfLayers === WeddingCakeLayers.More) {
											weddingCakeSize = WeddingCakeSize.Portions90Plus;
										} else {
											weddingCakeSize = undefined;
										}

										return undefined;
									}}
								>
									{weddingCakeLayersNameMap[layers][TENANT_LOCALE]}
								</LabelledRadioInput>
							{/each}
						</div>
					</div>
					<div>
						<p class="mb-1 font-semibold">Kolik potřebujete porcí?</p>
						<div class="mt-4 grid grid-cols-1 gap-x-3 gap-y-3 sm:grid-cols-2 md:grid-cols-5">
							{#each cakeOptions[numberOfLayers] as cakeSize}
								{@const disabled = !weddingCakeLayersToSizesMap[numberOfLayers].includes(cakeSize)}
								<LabelledRadioInput
									id="size-{cakeSize}"
									name="size"
									value={cakeSize}
									bind:group={weddingCakeSize}
									required
									{disabled}
									title={disabled ? "Pro tuto možnost zvolte jiný počet pater" : undefined}
								>
									{weddingCakeSizeShortNameMap[cakeSize][TENANT_LOCALE]}
								</LabelledRadioInput>
							{/each}
						</div>
					</div>
					<div>
						<p class="mb-1 font-semibold">Vzhled dortu</p>
						<div class="mt-4 grid grid-cols-1 gap-x-3 gap-y-3 sm:grid-cols-2 md:grid-cols-5">
							{#each Object.values(WeddingCakeDecorationStyle) as decorationStyle}
								<LabelledRadioInput
									id="decorationStyle-{decorationStyle}"
									name="decorationStyle"
									value={decorationStyle}
									bind:group={weddingDecorationStyle}
									required
								>
									{weddingCakeDecorationStyleNameMap[decorationStyle][TENANT_LOCALE]}
								</LabelledRadioInput>
							{/each}
						</div>
					</div>
					<div class="relative mt-4 w-full">
						<div class="grid w-full items-end gap-x-6 lg:grid-flow-col lg:grid-cols-2">
							<div>
								<Checkbox
									id="fillingTasteTest"
									class="relative !mt-0 h-[3.5rem] items-center rounded-[.25rem] border-[0.0625rem] bg-white px-3 text-base text-black lg:px-6"
									labelProps={{
										class:
											"absolute inset-0 w-full h-full cursor-pointer flex items-center pl-10 lg:pl-14 peer-checked:font-semibold",
									}}
								>
									<span class="!mt-[.15rem]"> Mám zájem o ochutnávku náplní </span>
								</Checkbox>
							</div>
						</div>
					</div>
				</div>
				<div class="flex flex-col gap-x-6 gap-y-3">
					<p class="mb-1">
						<span class="font-semibold">Máte konkrétní představu, jak by váš dort mohl vypadat?</span><br /> Nahrajte nám
						až 3 fotky vašeho vysněného dortu.
					</p>
					{#key fileUploaderKey}
						<FileUploader
							imagesInRow
							accept={acceptedStaticImages}
							destinationFolder="wedding-photos"
							bucket="upload"
							multiple
							maximum={3}
							on:change={setPhotoFile}
						/>
					{/key}
				</div>
				<div class="flex flex-col gap-x-6 gap-y-3">
					<p class="mb-1 font-semibold">Kontaktní údaje</p>
					<LabelledInput id="fullName" required autocomplete="name">Jméno a příjmení</LabelledInput>
					<LabelledInput id="email" required type="email" autocomplete="email">E-mail</LabelledInput>
					<LabelledInput
						id="phoneNumber"
						required
						type="tel"
						autocomplete="tel"
						inputProps={{ pattern: "[+]?[\\d ]+", title: "Telefonní číslo" }}
					>
						Telefon
					</LabelledInput>
				</div>
				<div class="grid grid-cols-1 gap-6 md:grid-cols-2">
					<div class="flex flex-col gap-x-6 gap-y-3">
						<p class="mb-1 font-semibold">Fakturační údaje</p>

						<div class="row-span-2 grid gap-3 sm:row-span-1 sm:grid-cols-[1fr,7.5rem]">
							<LabelledInput
								id="street"
								required
								autocomplete="billing address-line1"
								inputProps={{ class: "smartform-company-address-street" }}
							>
								Ulice
							</LabelledInput>
							<LabelledInput
								id="streetNumber"
								required
								autocomplete="billing address-line2"
								inputProps={{ class: "smartform-company-address-number" }}
							>
								čp.
							</LabelledInput>
						</div>
						<div class="row-span-2 grid gap-3 sm:row-span-1 sm:grid-cols-[1fr,7.5rem]">
							<LabelledInput
								id="city"
								required
								autocomplete="billing address-level2"
								inputProps={{ class: "smartform-company-address-city" }}
							>
								Město
							</LabelledInput>
							<LabelledInput
								id="zip"
								required
								minlength={3}
								autocomplete="billing postal-code"
								inputProps={{ class: "smartform-company-address-zip" }}
							>
								PSČ
							</LabelledInput>
						</div>
					</div>
					<div class="flex flex-col gap-x-6 gap-y-3">
						<Checkbox class="!mt-0 mb-2" id="asCompany" bind:checked={asCompany}>Fakturuji na firmu</Checkbox>
						<div
							class="contents {asCompany
								? ''
								: '[&>*]:pointer-events-none [&>*]:absolute [&>*]:opacity-0 [&>*]:lg:relative'}"
						>
							<LabelledInput
								id="cmpName"
								required
								disabled={!asCompany}
								inputProps={{ class: "smartform-company-name" }}
							>
								Název firmy
							</LabelledInput>
							<div class="row-span-2 grid gap-3 sm:row-span-1 sm:grid-cols-2">
								<LabelledInput
									id="cmpCin"
									required
									disabled={!asCompany}
									inputProps={{
										pattern: "\\d+",
										class: "smartform-company-registration-number",
									}}
								>
									IČO
								</LabelledInput>
								<LabelledInput id="cmpVat" disabled={!asCompany} inputProps={{ class: "smartform-company-vat-number" }}>
									DIČ
								</LabelledInput>
							</div>
						</div>
					</div>
				</div>
				<div class="relative flex flex-col gap-x-6 gap-y-3">
					<label class="mb-1 font-semibold" for="note">Poznámka k objednávce</label>
					<textarea
						id="note"
						name="note"
						class="input-with-label peer h-[15.5625rem] w-full rounded-[.25rem] border border-gray-200 px-3 !pb-4 !pt-[2.25rem] text-base focus:font-semibold focus:outline-none lg:px-6"
						placeholder=" "
					></textarea>
					<p class="text-2xs mt-3">*Povinné údaje</p>
				</div>
				<GdprCheckbox />

				<Button
					class="sm:!text-md mx-auto !rounded-full !px-8 !py-2 sm:!px-12 sm:!py-4 lg:mx-0 xl:!px-28 xl:!py-[1.125rem] xl:!text-[1.375rem]"
					type="submit"
					icon={cake}
					iconClass="h-6"
					disabled={$send.status === "loading"}
				>
					{#if $send.status === "loading"}
						Odesílám…
					{:else}
						Odeslat formulář
					{/if}
				</Button>
			</div>

			{#if sendErrorMessage}
				<Message variant="error">
					{nbspify(sendErrorMessage)}
				</Message>
			{/if}
		</div>
	</Form>
</Smartform>
